import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Grid,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Block as BlockIcon,
  Category as CategoryIcon,
  DirectionsCar as CarIcon,
  Add as AddIcon,
  Info as InfoIcon,
  Build as MaintenanceIcon,
} from '@mui/icons-material';
import { stopSellService, vehicleService, vehicleCategoryService, StopSellEntry } from '../services/api';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale/fr';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1920&q=80';

interface StopSellForm {
  date_debut: string;
  date_fin: string;
  seuil: string;
  note: string;
}

const emptyForm = (): StopSellForm => ({ date_debut: '', date_fin: '', seuil: '', note: '' });

const toDateStr = (d: string | null | undefined): string => (d ? d.slice(0, 10) : '');

const getStatusChip = (entry: StopSellEntry) => {
  if (entry.maintenance) {
    return <Chip icon={<MaintenanceIcon />} label="Maintenance" size="small" color="warning" sx={{ fontSize: '0.65rem' }} />;
  }
  const today = new Date().toISOString().slice(0, 10);
  const debut = toDateStr(entry.date_debut);
  const fin   = entry.date_fin ? toDateStr(entry.date_fin) : null;
  if (!fin || fin >= today) {
    if (debut <= today) return <Chip label="Actif" size="small" color="error" />;
    return <Chip label="À venir" size="small" color="default" />;
  }
  return <Chip label="Expiré" size="small" sx={{ opacity: 0.5 }} />;
};

const StopSell: React.FC = () => {
  const [stopSells, setStopSells] = useState<StopSellEntry[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Category form
  const [catForm, setCatForm] = useState<StopSellForm>(emptyForm());
  const [catId, setCatId] = useState<number | ''>('');
  const [savingCat, setSavingCat] = useState(false);

  // Vehicle form
  const [vehForm, setVehForm] = useState<StopSellForm>(emptyForm());
  const [vehId, setVehId] = useState<number | ''>('');
  const [savingVeh, setSavingVeh] = useState(false);

  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [ssRes, vRes, catRes] = await Promise.all([
        stopSellService.getAll(),
        vehicleService.getAll(),
        vehicleCategoryService.getAll(),
      ]);
      setStopSells(ssRes.data.data || []);
      setVehicles(vRes.data.data || []);
      const cats = (catRes.data.data || []).map((c: any) => ({ id: Number(c.id), name: c.name?.trim() || `Catégorie #${c.id}` }));
      cats.sort((a: { id: number; name: string }, b: { id: number; name: string }) => a.name.localeCompare(b.name, 'fr'));
      setCategories(cats);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleAddCategory = async () => {
    if (!catId || !catForm.date_debut) { setError('Sélectionnez une catégorie et une date de début.'); return; }
    try {
      setSavingCat(true); setError(null);
      await stopSellService.create({
        id_cat: Number(catId),
        date_debut: catForm.date_debut,
        date_fin: catForm.date_fin || null,
        seuil: catForm.seuil ? Number(catForm.seuil) : null,
        note: catForm.note || null,
      });
      setCatForm(emptyForm()); setCatId('');
      setSuccess('Stop sell catégorie ajouté.');
      setTimeout(() => setSuccess(null), 3000);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally { setSavingCat(false); }
  };

  const handleAddVehicle = async () => {
    if (!vehId || !vehForm.date_debut) { setError('Sélectionnez un véhicule et une date de début.'); return; }
    try {
      setSavingVeh(true); setError(null);
      await stopSellService.create({
        id_veh: Number(vehId),
        date_debut: vehForm.date_debut,
        date_fin: vehForm.date_fin || null,
        seuil: vehForm.seuil ? Number(vehForm.seuil) : null,
        note: vehForm.note || null,
      });
      setVehForm(emptyForm()); setVehId('');
      setSuccess('Stop sell véhicule ajouté.');
      setTimeout(() => setSuccess(null), 3000);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally { setSavingVeh(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer ce stop sell ?')) return;
    try {
      setDeletingId(id);
      await stopSellService.delete(id);
      setStopSells((prev) => prev.filter((s) => s.id !== id));
      setSuccess('Stop sell supprimé.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression.');
    } finally { setDeletingId(null); }
  };

  const catStopSells = stopSells.filter((s) => s.id_cat !== null && !s.maintenance);
  const vehStopSells = stopSells.filter((s) => s.id_veh !== null && !s.maintenance);
  const maintenanceStopSells = stopSells.filter((s) => s.maintenance);

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    try { return format(parseISO(toDateStr(d)), 'dd/MM/yyyy', { locale: fr }); }
    catch { return d; }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative', height: { xs: 140, md: 180 },
          backgroundImage: `linear-gradient(135deg, rgba(183,28,28,0.92) 0%, rgba(198,40,40,0.85) 100%), url(${HERO_IMAGE})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <BlockIcon sx={{ color: 'white', fontSize: 32 }} />
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, letterSpacing: '-0.02em' }}>
            Stop Sell
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)' }}>
          Gérez les périodes de restriction de réservation par catégorie ou véhicule
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, pb: 6 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Info banner */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          <strong>Règles :</strong> Un stop sell bloque la réservation si les dates de location chevauchent la période.
          Le <strong>seuil</strong> permet d'autoriser les locations d'une durée minimum (ex: 7 jours minimum en haute saison).
          Les stop sells <strong>maintenance</strong> sont créés/supprimés automatiquement lors du démarrage/fin d'une intervention.
        </Alert>

        {/* Two panels */}
        <Grid container spacing={3}>
          {/* Left: Category */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper elevation={0} sx={{ border: '1px solid #e0e4ea', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#2c3e50', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CategoryIcon sx={{ color: 'white' }} />
                <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>Catégorie de véhicule</Typography>
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem' }}>
                  Le champ <strong>Seuil location</strong> indique le nombre minimal de jours de location pour que le véhicule soit réservable.
                  Si le champ est non renseigné, le stop sell sera appliqué de manière stricte sur la période définie.
                  <br />* champs <strong>obligatoires</strong>
                  <br />
                  <strong>Catégories :</strong> la liste provient des catégories réellement assignées à vos véhicules (même identifiant qu’à la réservation — pas seulement les catégories « actives » du catalogue).
                </Alert>
                {!loading && categories.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2, fontSize: '0.78rem' }}>
                    Aucune catégorie de véhicule définie. Créez d'abord des catégories dans la configuration.
                  </Alert>
                )}

                {/* Add form */}
                <TableContainer sx={{ mb: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f6fa' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Catégorie *</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Début *</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Fin</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Seuil location</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Add row */}
                      <TableRow>
                        <TableCell sx={{ minWidth: 140 }}>
                          <FormControl size="small" fullWidth>
                            <Select value={catId} onChange={(e) => setCatId(e.target.value as number)} displayEmpty sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}>
                              <MenuItem value="" disabled><em>Sélectionner…</em></MenuItem>
                              {categories.map((c) => <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8rem' }}>{c.name}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <TextField size="small" type="date" value={catForm.date_debut} onChange={(e) => setCatForm((f) => ({ ...f, date_debut: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' }, width: 130 }} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <TextField size="small" type="date" value={catForm.date_fin} onChange={(e) => setCatForm((f) => ({ ...f, date_fin: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' }, width: 130 }} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 110 }}>
                          <TextField
                            size="small" type="number" value={catForm.seuil}
                            onChange={(e) => setCatForm((f) => ({ ...f, seuil: e.target.value }))}
                            inputProps={{ min: 1, max: 365 }}
                            InputProps={{ endAdornment: <InputAdornment position="end"><Typography variant="caption">jours</Typography></InputAdornment> }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' }, width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Button variant="contained" size="small" startIcon={savingCat ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <AddIcon />} onClick={handleAddCategory} disabled={savingCat} sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700, bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' }, whiteSpace: 'nowrap', px: 1.5 }}>
                            + Ajouter
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Existing category stop sells */}
                      {loading ? (
                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                      ) : catStopSells.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2, color: 'text.secondary', fontSize: '0.8rem' }}>Aucun stop sell par catégorie</TableCell></TableRow>
                      ) : catStopSells.map((ss) => (
                        <TableRow key={ss.id} hover sx={{ opacity: ss.date_fin && toDateStr(ss.date_fin) < new Date().toISOString().slice(0, 10) ? 0.55 : 1 }}>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CategoryIcon sx={{ fontSize: 14, color: '#7b1fa2' }} />
                              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>{ss.category?.name ?? `Cat. ${ss.id_cat}`}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(ss.date_debut)}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(ss.date_fin)}</TableCell>
                          <TableCell>
                            {ss.seuil ? <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{ss.seuil} jours</Typography> : <Typography variant="caption" color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getStatusChip(ss)}
                              <Tooltip title="Supprimer">
                                <IconButton size="small" color="error" onClick={() => handleDelete(ss.id)} disabled={deletingId === ss.id}>
                                  {deletingId === ss.id ? <CircularProgress size={14} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Right: Vehicle */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper elevation={0} sx={{ border: '1px solid #e0e4ea', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#1565c0', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CarIcon sx={{ color: 'white' }} />
                <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>Modèle de véhicule</Typography>
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem' }}>
                  Le champ <strong>Seuil location</strong> indique le nombre minimal de jours de location pour que le véhicule soit réservable.
                  Si le champ est non renseigné, le stop sell sera appliqué de manière stricte sur la période définie.
                  <br />* champs <strong>obligatoires</strong>
                </Alert>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f6fa' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Véhicule *</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Début *</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Fin</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Seuil location</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Add row */}
                      <TableRow>
                        <TableCell sx={{ minWidth: 160 }}>
                          <FormControl size="small" fullWidth>
                            <Select value={vehId} onChange={(e) => setVehId(e.target.value as number)} displayEmpty sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}>
                              <MenuItem value="" disabled><em>Sélectionner…</em></MenuItem>
                              {vehicles.map((v: any) => (
                                <MenuItem key={v.id} value={v.id} sx={{ fontSize: '0.8rem' }}>
                                  {v.registration_number} — {v.marque} {v.modele}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <TextField size="small" type="date" value={vehForm.date_debut} onChange={(e) => setVehForm((f) => ({ ...f, date_debut: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' }, width: 130 }} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <TextField size="small" type="date" value={vehForm.date_fin} onChange={(e) => setVehForm((f) => ({ ...f, date_fin: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' }, width: 130 }} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 110 }}>
                          <TextField
                            size="small" type="number" value={vehForm.seuil}
                            onChange={(e) => setVehForm((f) => ({ ...f, seuil: e.target.value }))}
                            inputProps={{ min: 1, max: 365 }}
                            InputProps={{ endAdornment: <InputAdornment position="end"><Typography variant="caption">jours</Typography></InputAdornment> }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' }, width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Button variant="contained" size="small" startIcon={savingVeh ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <AddIcon />} onClick={handleAddVehicle} disabled={savingVeh} sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' }, whiteSpace: 'nowrap', px: 1.5 }}>
                            + Ajouter
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Existing vehicle stop sells */}
                      {loading ? (
                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                      ) : vehStopSells.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2, color: 'text.secondary', fontSize: '0.8rem' }}>Aucun stop sell par véhicule</TableCell></TableRow>
                      ) : vehStopSells.map((ss) => (
                        <TableRow key={ss.id} hover sx={{ opacity: ss.date_fin && toDateStr(ss.date_fin) < new Date().toISOString().slice(0, 10) ? 0.55 : 1 }}>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CarIcon sx={{ fontSize: 14, color: '#1565c0' }} />
                              <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                  {ss.vehicle?.registration_number ?? `Veh. ${ss.id_veh}`}
                                </Typography>
                                {ss.vehicle && (
                                  <Typography variant="caption" color="text.secondary">
                                    {ss.vehicle.marque} {ss.vehicle.modele}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(ss.date_debut)}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(ss.date_fin)}</TableCell>
                          <TableCell>
                            {ss.seuil ? <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{ss.seuil} jours</Typography> : <Typography variant="caption" color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getStatusChip(ss)}
                              <Tooltip title="Supprimer">
                                <IconButton size="small" color="error" onClick={() => handleDelete(ss.id)} disabled={deletingId === ss.id}>
                                  {deletingId === ss.id ? <CircularProgress size={14} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Maintenance stop sells (read-only) */}
        {maintenanceStopSells.length > 0 && (
          <Paper elevation={0} sx={{ border: '1px solid #ffe0b2', borderRadius: 3, overflow: 'hidden', mt: 3 }}>
            <Box sx={{ bgcolor: '#e65100', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MaintenanceIcon sx={{ color: 'white' }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                Stop sells maintenance actifs ({maintenanceStopSells.length})
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', ml: 1 }}>
                Créés automatiquement — supprimés lorsque la maintenance est terminée
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fff3e0' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Véhicule</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Depuis</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Fin</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Note</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {maintenanceStopSells.map((ss) => (
                    <TableRow key={ss.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <MaintenanceIcon sx={{ fontSize: 14, color: '#e65100' }} />
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                            {ss.vehicle?.registration_number ?? `Veh. ${ss.id_veh}`} {ss.vehicle ? `— ${ss.vehicle.marque} ${ss.vehicle.modele}` : ''}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(ss.date_debut)}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        <Chip label="Ouvert" size="small" color="warning" sx={{ fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{ss.note ?? '—'}</TableCell>
                      <TableCell><Chip icon={<MaintenanceIcon />} label="En maintenance" size="small" color="warning" sx={{ fontSize: '0.65rem' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default StopSell;
